/**
 * Options interface for web game launch URL construction.
 */
export interface WebGameLaunchOptions {
  baseUrl: string;
  roomId: string;
  expectedHostUserId?: string;
  moderatorUserId?: string;
  isQuickPlay?: boolean;
  winScore?: number;
  loseScore?: number;
  timer?: number;
  mode?: string;
  [key: string]: any;
}

/*
 * Builds a web game launch URL with query parameters for game boards / rooms.
 * Supports expectedHostUserId, isQuickPlay, winScore, loseScore, timer, mode,
 * and additional custom parameters.
 */
export function buildWebGameLaunchUrl(options: WebGameLaunchOptions): string {
  const base = (options.baseUrl || '').trim();
  const roomId = (options.roomId || '').trim();

  let url: URL;

  try {
    if (base.includes('/game-board') || base.includes('/room')) {
      const cleanBase = base.endsWith('/') ? base : `${base}/`;
      url = new URL(`${cleanBase}${roomId}`);
    } else {
      const cleanBase = base.endsWith('/') ? base : `${base}/`;
      url = new URL(`game-board/${roomId}`, cleanBase);
    }
  } catch (e) {
    url = new URL(`https://spades.rivalforge.com/game-board/${roomId}`);
  }

  // Set expectedHostUserId from moderatorUserId or expectedHostUserId
  const hostId = options.expectedHostUserId || options.moderatorUserId;
  if (hostId) {
    url.searchParams.set('expectedHostUserId', hostId);
  }

  if (options.isQuickPlay) {
    url.searchParams.set('isQuickPlay', 'true');
  }

  if (options.winScore !== undefined && options.winScore !== null) {
    url.searchParams.set('winScore', options.winScore.toString());
  }

  if (options.loseScore !== undefined && options.loseScore !== null) {
    url.searchParams.set('loseScore', options.loseScore.toString());
  }

  if (options.timer !== undefined && options.timer !== null) {
    url.searchParams.set('timer', options.timer.toString());
  }

  if (options.mode) {
    url.searchParams.set('mode', options.mode);
  }

  return url.toString();
}

/*
 * Alias for buildWebGameLaunchUrl specifically for Spades game launches.
 */
export function buildSpadesLaunchUrl(options: WebGameLaunchOptions): string {
  return buildWebGameLaunchUrl(options);
}
